/* VoiceSearchSettings — user-facing opt-in panel. Shown on Profile when
 * the admin has voice search globally enabled. Two toggles:
 *   • Voice search on/off (FAB visibility + command listener)
 *   • Always listen for wake word ("Hey Recipe" / "Hey Recipe Filer" /
 *     "Hey Filer"). Costs battery, so default OFF.
 */
import { useVoiceSearch } from "@/contexts/VoiceSearchContext";
import { Microphone, SpeakerHigh } from "@phosphor-icons/react";

export default function VoiceSearchSettings() {
  const { prefsEnabled, wakeEnabled, setPrefsEnabled, setWakeEnabled, openOverlay } = useVoiceSearch();

  return (
    <section className="card-recipe" data-testid="profile-voice-settings">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 rounded-full bg-rf-amber/20 flex items-center justify-center">
          <Microphone size={20} weight="duotone" className="text-rf-orange" />
        </div>
        <h2 className="font-display text-2xl font-black text-rf-ink">Voice search</h2>
      </div>
      <p className="text-sm text-rf-ink-soft mb-5 leading-relaxed">
        Open any page, add a recipe, or jump into cook mode — hands free. The site-wide voice overlay
        is <b>separate</b> from Kitchen View&apos;s cook commands (next step, show ingredients, scroll
        down, etc.) — those always work on the cooking screen regardless of the switches below.
      </p>

      <label className="flex items-start gap-3 p-4 rounded-xl bg-rf-cream-alt cursor-pointer mb-3" data-testid="voice-search-pref-row">
        <input
          type="checkbox"
          data-testid="voice-search-pref"
          checked={prefsEnabled}
          onChange={(e) => setPrefsEnabled(e.target.checked)}
          className="mt-1 w-5 h-5 accent-rf-orange"
          aria-describedby="voice-pref-help"
        />
        <div className="flex-1">
          <p className="font-bold text-rf-ink">Enable voice search</p>
          <p id="voice-pref-help" className="text-xs text-rf-ink-soft mt-1">
            Shows a floating microphone button at the bottom-right of every page (hidden inside Kitchen View). Tap it, say what you want — &ldquo;my cookbooks&rdquo;, &ldquo;cook paella&rdquo;, &ldquo;scan a recipe&rdquo; — and we jump you straight there.
          </p>
        </div>
      </label>

      <label className={`flex items-start gap-3 p-4 rounded-xl bg-rf-cream-alt cursor-pointer mb-3 ${!prefsEnabled ? "opacity-50 pointer-events-none" : ""}`} data-testid="voice-wake-pref-row">
        <input
          type="checkbox"
          data-testid="voice-wake-pref"
          checked={wakeEnabled}
          onChange={(e) => setWakeEnabled(e.target.checked)}
          className="mt-1 w-5 h-5 accent-rf-orange"
          disabled={!prefsEnabled}
          aria-describedby="voice-wake-help"
        />
        <div className="flex-1">
          <p className="font-bold text-rf-ink flex items-center gap-2">
            <SpeakerHigh size={16} weight="duotone" />
            Always listen for the wake word
          </p>
          <p id="voice-wake-help" className="text-xs text-rf-ink-soft mt-1">
            When on, we quietly listen for <b>&ldquo;Hey Recipe&rdquo;</b>, <b>&ldquo;Hey Recipe Filer&rdquo;</b> or <b>&ldquo;Hey Filer&rdquo;</b> &mdash; any of the three opens the voice overlay instantly. Costs a little extra battery, so this is off by default. Needs voice search enabled above.
          </p>
        </div>
      </label>

      {prefsEnabled && (
        <button
          type="button"
          onClick={openOverlay}
          data-testid="voice-search-try-btn"
          className="btn-amber !py-2 !px-4 !text-sm"
        >
          <Microphone size={14} weight="bold" className="inline mr-1.5" />
          Try it now
        </button>
      )}
    </section>
  );
}
